import { ADMIN_BASE_PATH } from '@/lib/admin-paths';
import {
  canAccessOrganizationDashboard,
  canAccessOrganizationPath,
  isOrganizationStaffRole,
  type OrganizationAccessUser,
} from '@/lib/organization-access';
import {
  getEffectiveRole,
  isAdminRole,
  isJuryRole,
  isOrganizerRole,
} from '@/lib/rbac';
import {
  isPersonalParticipantDashboardPath,
  isRedirectAllowedForUser,
  type RoleDashboardUser,
} from '@/lib/role-dashboard';

export type DashboardAuthResult =
  | { status: 'loading' }
  | { status: 'unauthenticated'; loginUrl: string }
  | { status: 'forbidden' }
  | { status: 'allowed' };

function normalizePath(path: string): string {
  return path.split('?')[0].replace(/\/$/, '') || '/';
}

function buildLoginUrl(pathname: string, search = ''): string {
  const redirect = encodeURIComponent(`${pathname}${search}`);
  return `/login?redirect=${redirect}`;
}

/** Whether pathname is under the authenticated dashboard zone. */
export function isDashboardProtectedPath(pathname: string): boolean {
  const path = normalizePath(pathname);
  return (
    path === '/dashboard' ||
    path.startsWith('/dashboard/') ||
    path === '/jury' ||
    path.startsWith('/jury/')
  );
}

function isEventWorkspacePath(path: string): boolean {
  const parts = path.split('/').filter(Boolean);
  return parts[0] === 'dashboard' && parts[1] === 'events' && Boolean(parts[2]) && parts[2] !== 'new';
}

/** Admin who also has organizer or organization capabilities may open event workspace. */
function canUseOrganizerEventWorkspace(user: RoleDashboardUser): boolean {
  const role = getEffectiveRole(user);
  if (isOrganizerRole(role) || isOrganizationStaffRole(role)) {
    return true;
  }
  const roles = Array.isArray(user.roles) ? user.roles : [];
  return roles.some((r) => isOrganizerRole(r) || isOrganizationStaffRole(r));
}

/**
 * Resolves auth before rendering dashboard shell.
 * Unauthenticated → login redirect URL.
 * Authenticated but wrong role → forbidden.
 */
export function resolveDashboardAuth(
  pathname: string | null | undefined,
  user: RoleDashboardUser | null | undefined,
  options?: { hydrated?: boolean; authChecking?: boolean }
): DashboardAuthResult {
  if (!pathname || !isDashboardProtectedPath(pathname)) {
    return { status: 'allowed' };
  }

  if (!options?.hydrated || options?.authChecking) {
    return { status: 'loading' };
  }

  if (!user) {
    return { status: 'unauthenticated', loginUrl: buildLoginUrl(pathname) };
  }

  const path = normalizePath(pathname);
  const role = getEffectiveRole(user);
  const isAdmin = Boolean(user.master_admin) || isAdminRole(role);

  if (path.startsWith(ADMIN_BASE_PATH) || path.startsWith('/admin')) {
    return isAdmin ? { status: 'allowed' } : { status: 'forbidden' };
  }

  if (isAdmin) {
    // Platform-only admins use admin APIs — not organizer event workspace deep links.
    // Dual-role users (admin + organizer/org) may still enter event workspace.
    if (isEventWorkspacePath(path) && !canUseOrganizerEventWorkspace(user)) {
      return { status: 'forbidden' };
    }
    return { status: 'allowed' };
  }

  if (path.startsWith('/dashboard/organization')) {
    const orgUser = user as OrganizationAccessUser;
    if (!canAccessOrganizationDashboard(orgUser)) {
      return { status: 'forbidden' };
    }
    if (!canAccessOrganizationPath(orgUser, path)) {
      return { status: 'forbidden' };
    }
    return { status: 'allowed' };
  }

  if (isOrganizationStaffRole(role) && !isEventWorkspacePath(path)) {
    if (isPersonalParticipantDashboardPath(path)) {
      return { status: 'allowed' };
    }
    if (path === '/dashboard/certificates' || path.startsWith('/dashboard/certificates/')) {
      return { status: 'forbidden' };
    }
    const orgAllowed =
      path === '/dashboard' ||
      path.startsWith('/dashboard/calendar') ||
      path.startsWith('/dashboard/events') ||
      path.startsWith('/dashboard/forms') ||
      path === '/dashboard/notifications' ||
      path === '/dashboard/support' ||
      path === '/dashboard/organizer/support' ||
      path.startsWith('/dashboard/organizer');
    if (!orgAllowed && !isRedirectAllowedForUser(user, path)) {
      return { status: 'forbidden' };
    }
  }

  if (path.startsWith('/dashboard/organizer')) {
    if (path === '/dashboard/organizer/support') {
      return { status: 'allowed' };
    }
    const approvalStatus = String(user?.approval_status || '').toUpperCase();
    if (approvalStatus === 'PENDING' || user?.is_approval_pending) {
      return { status: 'forbidden' };
    }
    if (approvalStatus === 'REJECTED' || user?.is_approval_rejected) {
      return { status: 'forbidden' };
    }
    if (isOrganizationStaffRole(role) || isOrganizerRole(role)) {
      return { status: 'allowed' };
    }
  }

  if (path.startsWith('/jury')) {
    return { status: 'allowed' };
  }

  if (isJuryRole(role) && path.startsWith('/dashboard')) {
    return { status: 'forbidden' };
  }

  if (!isRedirectAllowedForUser(user, path)) {
    return { status: 'forbidden' };
  }

  return { status: 'allowed' };
}
